import { isMenu, item, tMenu } from 'src/shared/frontendTypes/navigation'

export const NAVIGATION_CACHE_SCHEMA = 2
export const NAVIGATION_REVALIDATE_MS = 60_000
const FAILURE_RETRY_MS = 30_000
const REQUEST_TIMEOUT_MS = 8_000

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const cleanTitle = (value: unknown) => {
  const title = typeof value === 'string' ? value.trim()
    : typeof value === 'number' && Number.isFinite(value) ? String(value) : ''
  return title === '0' ? '' : title
}
const text = (value: unknown) => typeof value === 'string' && value.trim() ? value.trim() : undefined
const positiveNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}
const normalizeItems = (value: unknown, parent = 0, nextId = { current: 1 }): tMenu => {
  if (!Array.isArray(value)) return []
  return value.flatMap(raw => {
    if (!isRecord(raw)) return []
    const id = positiveNumber(raw.id) || nextId.current
    nextId.current = Math.max(nextId.current, id + 1)
    const children = normalizeItems(raw.children, id, nextId)
    const title = cleanTitle(raw.title)
    const href = text(raw.href), path = text(raw.path)
    const imageSrc = text(raw.imageSrc), imageAlt = text(raw.imageAlt)
    const menueId = positiveNumber(raw.menueId), brand = positiveNumber(raw.brand)
    if (!title && !href && !menueId && !brand) return children.map(child => ({ ...child, parent }))
    const entry: item = {
      id, parent: positiveNumber(raw.parent) || parent, title,
      ...(menueId ? { menueId } : {}), ...(brand ? { brand } : {}),
      ...(href ? { href } : {}), ...(path ? { path } : {}),
      ...(imageSrc ? { imageSrc } : {}), ...(imageAlt ? { imageAlt } : {}),
      ...(children.length ? { children } : {}),
    }
    return [entry]
  })
}

/** Keep the same link, brand and image contract for baked, cached and API data. */
export const normalizeNavigationPaths = (value: unknown): tMenu => {
  const paths = normalizeItems(value)
  return paths.length ? paths : isMenu(value, false) ? value : []
}

interface Scope { menuId: string; shopId: string; language: string; buildDate: string }
interface Options {
  scope: Scope
  initialPaths: unknown
  getVersion: (signal: AbortSignal) => Promise<unknown>
  getPaths: (signal: AbortSignal) => Promise<unknown>
  storage?: () => Pick<Storage, 'getItem' | 'setItem'> | undefined
  now?: () => number
}

/** One browser store per built tenant, shared by header, drawer, search and listing.
 * The server snapshot always stays baked so session data cannot break hydration. */
export const createNavigationCache = ({ scope, initialPaths, getVersion, getPaths, storage, now = Date.now }: Options) => {
  const baked = normalizeNavigationPaths(initialPaths)
  let paths = baked
  let changedAt = Date.parse(scope.buildDate) || 0
  let checkedAt = 0
  let attemptedAt: number | undefined
  let hydrated = false
  let pending: Promise<void> | undefined
  const listeners = new Set<() => void>()
  const identity = JSON.stringify([NAVIGATION_CACHE_SCHEMA, scope.shopId, scope.language, scope.menuId, scope.buildDate])
  const key = `navigation-paths:${identity}`
  const emit = () => listeners.forEach(listener => listener())
  const write = () => {
    try {
      storage?.()?.setItem(key, JSON.stringify({ identity, schema: NAVIGATION_CACHE_SCHEMA, changedAt, checkedAt, paths }))
    } catch (_error) { /* Storage is optional, including private browsing/quota limits. */ }
  }
  const hydrate = () => {
    if (hydrated) return
    hydrated = true
    try {
      const cached = JSON.parse(storage?.()?.getItem(key) || 'null')
      if (!isRecord(cached) || cached.identity !== identity || cached.schema !== NAVIGATION_CACHE_SCHEMA
        || typeof cached.changedAt !== 'number' || !Number.isFinite(cached.changedAt)
        || cached.changedAt < (Date.parse(scope.buildDate) || 0)
        || typeof cached.checkedAt !== 'number' || !Number.isFinite(cached.checkedAt)
        || cached.checkedAt > now() || !isMenu(cached.paths, false) || !cached.paths.length) return
      // A previous build's session cache is deliberately inaccessible via identity.
      paths = normalizeNavigationPaths(cached.paths)
      changedAt = cached.changedAt
      checkedAt = cached.checkedAt
      emit()
    } catch (_error) { /* Ignore old/malformed cache and retain embedded navigation. */ }
  }
  const refresh = (): Promise<void> => {
    hydrate()
    if (pending) return pending
    const time = now()
    if (checkedAt && time - checkedAt < NAVIGATION_REVALIDATE_MS) return Promise.resolve()
    if (attemptedAt !== undefined && time - attemptedAt < FAILURE_RETRY_MS) return Promise.resolve()
    attemptedAt = time
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    pending = (async () => {
      try {
        let version = changedAt
        if (paths.length) {
          const metadata = await getVersion(controller.signal)
          if (!isRecord(metadata) || typeof metadata.changedDate !== 'string') return
          version = Date.parse(metadata.changedDate)
          if (!Number.isFinite(version)) return
          if (version <= changedAt) {
            checkedAt = now()
            write()
            return
          }
        }
        if (controller.signal.aborted) return
        const next = normalizeNavigationPaths(await getPaths(controller.signal))
        if (!next.length || controller.signal.aborted) return
        paths = next
        changedAt = version
        checkedAt = now()
        write()
        emit()
      } catch (_error) {
        // Keep the immediately usable embedded/current menu on network failures.
      } finally {
        clearTimeout(timeout)
        pending = undefined
      }
    })()
    return pending
  }
  return {
    key,
    getSnapshot: () => paths,
    getServerSnapshot: () => baked,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } },
    refresh,
  }
}
